/**
 * UI / 壳层状态：视图切换、遮牌页、Toast、横幅、Sheet。
 *
 * 刻意与 tableStore 分开：牌桌状态是「服务端（或本地引擎）权威视图」的投影，
 * 而这里全是本机交互态。混在一起会导致每次 Toast 都让整张牌桌重渲染。
 *
 * 红线 #48 提醒：倒计时、滑块拖动、筹码滚动**不要**进这个 store，直接写 DOM ref。
 */

import { create } from 'zustand'
import type { SeatIndex } from '@poker/shared'

import type { Density } from '../table/seatGeometry.js'

export type AppView = 'lobby' | 'table'
export type SheetKind =
  | 'settings'
  | 'history'
  | 'roomInfo'
  | 'assist'
  /** 选座（旁观者坐下） */
  | 'seatPicker'
  /** 换座：同一张选座面板，空座发 changeSeat */
  | 'seatChange'
  /** 「我的座位」：暂离 / 换座 / 起立 / 离开房间 */
  | 'seatSelf'
  /** 玩家申请买分 */
  | 'topUp'
  /** 房主的买分审批队列 */
  | 'topUpQueue'
  /** 点了别人的座位牌之后的动作菜单（带 `sheetSeat`） */
  | 'seatMenu'
  /** 向某个座位求购筹码（带 `sheetSeat`） */
  | 'transfer'
  /** 别人向我求购的待批准列表 */
  | 'transferQueue'
  /** 转账流水，全员可见 */
  | 'transferLog'
  /** 结束牌局的二次确认（不可逆，必须确认） */
  | 'endGame'

/** 遮牌原因：初次进入 / 传手机 / 失焦。三者都必须是不透明纯色、零淡入（红线 #12） */
export type VeilReason = 'init' | 'pass' | 'blur'

export interface ToastMsg {
  readonly id: number
  readonly text: string
  readonly tone: 'info' | 'warn' | 'error' | 'success'
  /** 0 = 需手动关闭 */
  readonly durationMs: number
}

export interface UiState {
  view: AppView
  sheet: SheetKind | null
  /**
   * 面板作用于哪个座位（`seatMenu` / `transfer` 用）。
   *
   * 单独一个字段而不是把参数塞进 `sheet`：面板种类是个闭集合，
   * 做成 `'transfer:3'` 这样的串之后每处都要先解析一次，而且拼错了编译器发现不了。
   */
  sheetSeat: SeatIndex | null
  toast: ToastMsg | null
  /** 顶部 28px 非阻塞条（断线提示）。绝不用全屏模态挡牌桌。 */
  banner: string | null
  /** 快照到达时置 true，两帧后复位；期间全局禁 transition/animation */
  resync: boolean
  /** 本地传手机：遮牌页是否显示。刷新后必须回到 true（红线 #12 第 12 条） */
  veiled: boolean
  veilReason: VeilReason
  /** 遮牌页上展示的「该交给谁」 */
  veilTargetSeat: SeatIndex | null
  /** 横屏提示 */
  rotateHint: boolean
  /**
   * 当前密度档。App 首帧按视口估一个，牌桌挂载后由 useTableGeometry 实测覆盖。
   * 写在 .app-shell 的 data-density 上，tokens.css 据此切 --seat-w / --dock-h 等变量。
   */
  density: Density

  setView(view: AppView): void
  openSheet(sheet: SheetKind, seat?: SeatIndex | null): void
  closeSheet(): void
  showToast(text: string, tone?: ToastMsg['tone'], durationMs?: number): void
  dismissToast(): void
  setBanner(text: string | null): void
  setResync(on: boolean): void
  /** 遮牌：进入永远瞬时，退出由 CSS 的 .veil--leaving 负责 260ms 淡出 */
  veil(reason: VeilReason, targetSeat?: SeatIndex | null): void
  unveil(): void
  setRotateHint(on: boolean): void
  setDensity(d: Density): void
  reset(): void
}

let toastSeq = 0

export const useUiStore = create<UiState>((set) => ({
  view: 'lobby',
  sheet: null,
  sheetSeat: null,
  toast: null,
  banner: null,
  resync: false,
  // 初始即遮：刷新后直接渲染上一个人的底牌就是当面泄露
  veiled: true,
  veilReason: 'init',
  veilTargetSeat: null,
  rotateHint: false,
  density: 'normal',

  setView: (view) => set({ view }),
  openSheet: (sheet, seat = null) => set({ sheet, sheetSeat: seat }),
  closeSheet: () => set({ sheet: null, sheetSeat: null }),
  showToast: (text, tone = 'info', durationMs = 4000) =>
    set({ toast: { id: ++toastSeq, text, tone, durationMs } }),
  dismissToast: () => set({ toast: null }),
  setBanner: (banner) => set({ banner }),
  setResync: (resync) => set({ resync }),
  veil: (reason, targetSeat = null) =>
    set({ veiled: true, veilReason: reason, veilTargetSeat: targetSeat }),
  unveil: () => set({ veiled: false }),
  setRotateHint: (rotateHint) => set({ rotateHint }),
  setDensity: (density) => set({ density }),
  reset: () =>
    set({
      view: 'lobby',
      sheet: null,
      sheetSeat: null,
      toast: null,
      banner: null,
      resync: false,
      veiled: true,
      veilReason: 'init',
      veilTargetSeat: null,
      rotateHint: false,
    }),
}))
